import { useEffect, useState, useCallback, useRef, memo, useMemo } from "react";
import { List } from "react-window";
import { AutoSizer } from "react-virtualized-auto-sizer";
import LoadingInComponent from "../../Components/LoadingInComponent";
import Button from "../../Components/Button";
import { useToast } from "../../Components/Toast";
import axios from "axios";
import {
  GUEST_LIST_API,
  GUEST_BULK_UPDATE_API,
  DIETARY_OPTIONS_API,
  ALL_TABLE_OPTIONS_API,
  TAG_OPTIONS_API,
} from "../../Constant/api";
import { useNavigate } from "react-router-dom";
import { GUEST_LIST } from "../../Router/path";
import Breadcrumb from "../../Components/Breadcrumb";
import { salutations } from "../../Constant/salutation";
import { statusOptions } from "../../Constant/options";
import DropdownSet from "../../Components/DropdownSet";
import Paginator from "../../Components/Paginator";
import { Trash, Add, Maximize4, Refresh2, TickSquare, FilterSearch } from "iconsax-react";
import Modal from "../../Components/Modal";
import FilterTextInput from "../../Components/FilterTextInput";
import LastSavedIndicator from "../../Components/LastSavedIndicator";

const TABLE_WIDTH = 1710;
const HEADER_HEIGHT = 32;
const ROW_HEIGHT = 34;

const GuestRow = memo(({
  index,
  style,
  guestList,
  handleRemoveRow,
  handleRestoreRow,
  handleInputChange,
  safeDeletedGuestIds,
  safeModifiedGuests,
  autosavedGuestIds,
  tableOptions,
  tagOptions,
  dietaryOptions,
  validationErrors,
}) => {

  const guest = guestList[index];
  if (!guest) return null;

  const isNew = typeof guest.id === "string" && String(guest.id).startsWith("new-");
  const isDeleted = safeDeletedGuestIds.has(guest.id);
  const isModified = !!safeModifiedGuests[guest.id];
  const isAutosaved = autosavedGuestIds?.has(guest.id);
  const errorsForGuest = validationErrors?.[guest.id] || {};
  
  let rowClass = "flex items-center border-b border-[#EFF0F2] text-gray-900 transition-colors";
  if (isNew) rowClass += " bg-green-200 hover:bg-green-300";
  else if (isDeleted) rowClass += " bg-red-200 hover:bg-red-300";
  else if (isModified) rowClass += " bg-blue-200 hover:bg-blue-300";
  else rowClass += " bg-white hover:bg-gray-200";

  if (isAutosaved) {
    rowClass += " relative";
  }

  const isDisabled = isDeleted;
  const rowStyle = { ...style, display: 'flex', width: TABLE_WIDTH };

  return (
    <div style={rowStyle} className={rowClass}>
      {isAutosaved && (
        <div className="absolute inset-0 border-2 border-green-500 pointer-events-none z-50" />
      )}
      {/* Fixed Columns */}
      <div className="flex items-center sticky left-0 z-20 bg-inherit border-r border-[#EFF0F2]">
        <div className="flex items-center justify-center gap-1 w-[50px] shrink-0 px-[4px]">
          {!isDeleted ? (
            <button
              onClick={() => handleRemoveRow(guest.id)}
              className="text-red-500 hover:text-red-700"
              title={isNew ? "Remove Row" : "Delete Guest"}
            >
              <Trash size={14} />
            </button>
          ) : (
            <button
              onClick={() => handleRestoreRow(guest.id)}
              className="text-blue-500 hover:text-blue-700"
              title="Restore Guest"
            >
              <Refresh2 size={14} />
            </button>
          )}
          {isModified && !isDeleted && !isNew && (
            <div className="flex flex-col items-center">
              <button
                onClick={() => handleRestoreRow(guest.id)}
                className="text-blue-500 hover:text-blue-700"
                title="Reset Changes"
              >
                <Refresh2 size={14} />
              </button>
              {isAutosaved && (
                <span className="text-[10px] text-green-600 font-bold leading-none mt-0.5">
                  Saved
                </span>
              )}
            </div>
          )}
        </div>
        <div className="px-[4px] py-[2px] w-[80px] shrink-0">
          <select
            className={`w-full h-[28px] px-2 border ${errorsForGuest.salutation ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#5C4BF5]"} rounded focus:outline-none text-sm disabled:bg-gray-100`}
            value={guest.salutation || ""}
            onChange={(e) => handleInputChange(guest.id, "salutation", e.target.value)}
            disabled={isDisabled}
          >
            <option value="">None</option>
            {salutations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="px-[4px] py-[2px] w-[150px] shrink-0">
          <input
            type="text"
            className={`w-full p-1 border ${errorsForGuest.first_name ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#5C4BF5]"} rounded focus:outline-none text-sm disabled:bg-gray-100`}
            value={guest.first_name || ""}
            onChange={(e) => handleInputChange(guest.id, "first_name", e.target.value)}
            disabled={isDisabled}
          />
        </div>
        <div className="px-[4px] py-[2px] w-[150px] shrink-0">
          <input
            type="text"
            className={`w-full p-1 border ${errorsForGuest.last_name ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#5C4BF5]"} rounded focus:outline-none text-sm disabled:bg-gray-100`}
            value={guest.last_name || ""}
            onChange={(e) => handleInputChange(guest.id, "last_name", e.target.value)}
            disabled={isDisabled}
          />
        </div>
      </div>

      {/* Scrollable Columns */}
      <div className="px-[4px] py-[2px] w-[200px] shrink-0">
        <input
          type="email"
          className={`w-full p-1 border ${errorsForGuest.email ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#5C4BF5]"} rounded focus:outline-none text-sm disabled:bg-gray-100`}
          value={guest.email || ""}
          onChange={(e) => handleInputChange(guest.id, "email", e.target.value)}
          disabled={isDisabled}
        />
      </div>
      <div className="px-[4px] py-[2px] w-[150px] shrink-0">
        <input
          type="text"
          className={`w-full p-1 border ${errorsForGuest.contact ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#5C4BF5]"} rounded focus:outline-none text-sm disabled:bg-gray-100`}
          value={guest.contact || ""}
          onChange={(e) => handleInputChange(guest.id, "contact", e.target.value)}
          disabled={isDisabled}
        />
      </div>
      <div className="px-[4px] py-[2px] w-[150px] shrink-0">
        <input
          type="text"
          className={`w-full p-1 border ${errorsForGuest.group_name ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#5C4BF5]"} rounded focus:outline-none text-sm disabled:bg-gray-100`}
          value={guest.group_name || ""}
          onChange={(e) => handleInputChange(guest.id, "group_name", e.target.value)}
          disabled={isDisabled}
        />
      </div>
      <div className="px-[4px] py-[2px] w-[180px] shrink-0">
        <DropdownSet
          options={tableOptions}
          value={guest.table_id}
          hiddenLabel
          label="Table"
          onChange={(_, value) => handleInputChange(guest.id, "table_id", value)}
          disabled={isDisabled}
          compact
          className={errorsForGuest.table_id ? "border border-red-400 rounded" : ""}
        />
      </div>
      <div className="px-[4px] py-[2px] w-[220px] shrink-0">
        <DropdownSet
          options={tagOptions}
          value={guest.tag_ids}
          isMulti
          showMultiAsString
          hiddenLabel
          label="Tags"
          onChange={(_, value) => handleInputChange(guest.id, "tag_ids", value)}
          disabled={isDisabled}
          compact
          className={errorsForGuest.tag_ids ? "border border-red-400 rounded" : ""}
        />
      </div>
      <div className="px-[4px] py-[2px] w-[220px] shrink-0">
        <DropdownSet
          options={dietaryOptions}
          value={guest.dietary_ids}
          isMulti
          showMultiAsString
          hiddenLabel
          label="Dietary"
          onChange={(_, value) => handleInputChange(guest.id, "dietary_ids", value)}
          disabled={isDisabled}
          compact
          className={errorsForGuest.dietary_ids ? "border border-red-400 rounded" : ""}
        />
      </div>
      <div className="px-[4px] py-[2px] w-[120px] shrink-0">
        <select
          className={`w-full h-[28px] px-2 border ${errorsForGuest.status ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#5C4BF5]"} rounded focus:outline-none text-sm disabled:bg-gray-100`}
          value={guest.status}
          onChange={(e) => handleInputChange(guest.id, "status", e.target.value)}
          disabled={isDisabled}
        >
          {statusOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
        </select>
      </div>
    </div>
  );
});

const areGuestsEqual = (g1, g2) => {
  if (!g1 || !g2) return false;
  // Compare basic fields
  const fields = ['salutation', 'first_name', 'last_name', 'email', 'contact', 'group_name', 'table_id', 'note'];
  for (const f of fields) {
    // Use loose equality for things like table_id (string vs number) or null vs undefined
    if (g1[f] != g2[f] && !(g1[f] == null && g2[f] == null)) return false;
  }
  
  // Status check (normalize string "1"/"0" vs boolean or number)
  const s1 = g1.status === "1" || g1.status === 1 || g1.status === true;
  const s2 = g2.status === "1" || g2.status === 1 || g2.status === true;
  if (s1 !== s2) return false;

  // Array comparisons
  const compareArrays = (a, b) => {
    const arrA = Array.isArray(a) ? a : [];
    const arrB = Array.isArray(b) ? b : [];
    if (arrA.length !== arrB.length) return false;
    const sortedA = [...arrA].sort();
    const sortedB = [...arrB].sort();
    return sortedA.every((val, idx) => val == sortedB[idx]);
  };

  if (!compareArrays(g1.tag_ids, g2.tag_ids)) return false;
  if (!compareArrays(g1.dietary_ids, g2.dietary_ids)) return false;

  return true;
};

const GuestBulkEditPage = () => {
  const [loading, setLoading] = useState(false);
  const [apiGuestList, setApiGuestList] = useState([]);
  const [guestListMeta, setGuestListMeta] = useState({});
  const [perPage, setPerPage] = useState(2000);
  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [modifiedGuests, setModifiedGuests] = useState({});
  const [deletedGuestIds, setDeletedGuestIds] = useState(new Set());
  const [autosavedGuestIds, setAutosavedGuestIds] = useState(new Set());
  const [autosavedDeletedIds, setAutosavedDeletedIds] = useState(new Set());
  const [pendingGuestIds, setPendingGuestIds] = useState(new Set());
  const [pendingDeletedIds, setPendingDeletedIds] = useState(new Set());
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);
  const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(true);
  const [nextAutosaveTime, setNextAutosaveTime] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [originalGuests, setOriginalGuests] = useState({});
  const [isFabOpen, setIsFabOpen] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  
  const listRef = useRef(null);
  const headerScrollRef = useRef(null);
  const isSyncingScrollRef = useRef(false);
  const [listElement, setListElement] = useState(null);

  const safeModifiedGuests = useMemo(() => modifiedGuests || {}, [modifiedGuests]);
  const safeDeletedGuestIds = useMemo(() => deletedGuestIds || new Set(), [deletedGuestIds]);
  const newGuestCount = useMemo(() => {
    return Array.from(pendingGuestIds).filter(id => typeof id === "string" && id.startsWith("new-")).length;
  }, [pendingGuestIds]);

  const modifiedExistingCount = useMemo(() => {
    return Array.from(pendingGuestIds).filter(id => typeof id !== "string" || !id.startsWith("new-")).length;
  }, [pendingGuestIds]);

  const pendingDeletedCount = pendingDeletedIds.size;
  const hasUnsavedChanges = pendingGuestIds.size > 0 || pendingDeletedCount > 0;

  const [guestList, setGuestList] = useState([]);

  useEffect(() => {
    // 1. Transform API list with local modifications
    let transformed = apiGuestList.map(guest => {
      const modified = safeModifiedGuests[guest.id];
      if (modified) return { ...modified };

      return {
        ...guest,
        dietary_ids: guest.dietaries?.map(d => d.id) || [],
        tag_ids: guest.tags?.map(t => t.id) || [],
        status: guest.status ? "1" : "0"
      };
    });

    // 2. Prepend new guests
    const newGuests = Object.values(safeModifiedGuests)
      .filter(g => typeof g.id === "string" && g.id.startsWith("new-"));
    const newGuestsToPrepend = newGuests.filter(ng => !transformed.some(tg => tg.id === ng.id));
    
    // 3. Prepend deleted guests not in current page
    const deletedGuestsToPrepend = Array.from(safeDeletedGuestIds)
      .filter(id => !transformed.some(tg => tg.id === id))
      .map(id => originalGuests[id])
      .filter(Boolean);

    setGuestList([...newGuestsToPrepend, ...deletedGuestsToPrepend, ...transformed]);
  }, [apiGuestList, safeModifiedGuests, safeDeletedGuestIds, originalGuests]);

  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchGuestList = useCallback(
    async (page = 1, perPageValue = perPage) => {
      setLoading(true);
      axios
        .get(GUEST_LIST_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: {
              eventId: sessionStorage.getItem("event_id"),
              page: page,
              perPage: perPageValue,
              sortBy: "id",
              sortOrder: "desc",
              search: searchTerm,
              tags: selectedTags,
              tables: selectedTables,
              statuses: selectedStatus,
            },
        })
        .then((res) => {
          setLoading(false);
          const list = Array.isArray(res.data?.data) ? res.data.data : [];
          
          setApiGuestList(list);
          
          // Only update original guests for IDs we don't already have
          setOriginalGuests(prev => {
            const next = { ...prev };
            list.forEach(g => {
              if (!next[g.id]) {
                next[g.id] = {
                  ...g,
                  dietary_ids: g.dietaries?.map(d => d.id) || [],
                  tag_ids: g.tags?.map(t => t.id) || [],
                  status: g.status ? "1" : "0"
                };
              }
            });
            return next;
          });
          
          setGuestListMeta(res.data.meta.pagination);
        })
        .catch((err) => {
          setLoading(false);
          toast("Failed to fetch guest list.", "error");
          console.error(err);
        });
    },
    [perPage, toast, searchTerm, selectedTags, selectedTables, selectedStatus]
  );

  const isValidEmail = useCallback((email) => {
    const value = String(email || "").trim();
    if (!value) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }, []);

  const parseValidationErrors = useCallback((errorData, updates) => {
    const nextErrors = {};

    const addError = (index, field) => {
      const guestId = updates?.[index]?.id;
      if (!guestId || !field) return;
      if (!nextErrors[guestId]) nextErrors[guestId] = {};
      nextErrors[guestId][field] = true;
    };

    const parseKey = (key) => {
      const match = String(key || "").match(/guests\.(\d+)\.(\w+)/);
      if (!match) return;
      addError(parseInt(match[1], 10), match[2]);
    };

    if (typeof errorData?.data === "string") {
      const matches = errorData.data.matchAll(/guests\.(\d+)\.(\w+)/g);
      for (const match of matches) {
        addError(parseInt(match[1], 10), match[2]);
      }
    }

    const errorsObj =
      errorData?.errors ||
      errorData?.data?.errors ||
      (typeof errorData?.data === "object" && errorData?.data !== null ? errorData.data : null);

    if (errorsObj && typeof errorsObj === "object") {
      Object.keys(errorsObj).forEach(parseKey);
    }

    return nextErrors;
  }, []);

  const fetchOptions = useCallback(async () => {
    const headers = {
      Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
    };
    const params = { eventId: sessionStorage.getItem("event_id") };

    try {
      const [dietaryRes, tableRes, tagRes] = await Promise.all([
        axios.get(DIETARY_OPTIONS_API, { headers, params }),
        axios.get(ALL_TABLE_OPTIONS_API, { headers, params }),
        axios.get(TAG_OPTIONS_API, { headers, params }),
      ]);

      setDietaryOptions(dietaryRes.data.data);
      setTableOptions(tableRes.data.data);
      setTagOptions(tagRes.data.data);
    } catch (error) {
      console.error("Failed to fetch options", error);
      toast("Failed to fetch options for dropdowns", "error");
    }
  }, [toast]);

  useEffect(() => {
    fetchGuestList();
    fetchOptions();

    // Global click listener to help close dropdowns if they get stuck
    const handleGlobalClick = (e) => {
      // If we clicked on something that isn't part of a react-select component
      // and isn't an input/select that should handle its own focus
      if (!e.target.closest(".react-select") && 
          !e.target.closest(".react-select__menu") &&
          !e.target.closest(".react-select__menu-portal") &&
          e.target.tagName !== "INPUT" && 
          e.target.tagName !== "SELECT") {
        // Blur any active element to trigger react-select's onBlur/close
        if (document.activeElement && document.activeElement.blur) {
          document.activeElement.blur();
        }
      }
    };

    window.addEventListener("mousedown", handleGlobalClick);
    return () => window.removeEventListener("mousedown", handleGlobalClick);
  }, [fetchGuestList, fetchOptions]);

  const checkPendingLimit = useCallback((guestId) => {
    // If guest is already pending (update or delete), allow edit
    if (pendingGuestIds.has(guestId) || pendingDeletedIds.has(guestId)) return true;
    
    // Check total pending
    if (pendingGuestIds.size + pendingDeletedIds.size >= 100) {
        toast("Pending changes limit reached (100). Please save your changes.", "error");
        setShowSaveConfirmModal(true);
        return false;
    }
    return true;
  }, [pendingGuestIds, pendingDeletedIds, toast]);

  const handleAutoSave = useCallback(async () => {
    const pendingGuests = Array.from(pendingGuestIds)
      .map((id) => modifiedGuests[id])
      .filter(Boolean);

    const frontendErrors = {};
    const validGuests = [];

    pendingGuests.forEach((guest) => {
      const guestErrors = {};
      if (!String(guest.first_name || "").trim()) guestErrors.first_name = true;
      if (!String(guest.last_name || "").trim()) guestErrors.last_name = true;
      if (!isValidEmail(guest.email)) guestErrors.email = true;

      if (Object.keys(guestErrors).length > 0) {
        frontendErrors[guest.id] = guestErrors;
      } else {
        validGuests.push(guest);
      }
    });

    if (Object.keys(frontendErrors).length > 0) {
      setValidationErrors((prev) => ({ ...prev, ...frontendErrors }));
      setHasSaveError(true);
    }

    const updates = validGuests.map((guest) => {
      const payload = {
        id: guest.id,
        salutation: guest.salutation,
        first_name: guest.first_name,
        last_name: guest.last_name,
        email: guest.email,
        contact: guest.contact,
        group_name: guest.group_name,
        table_id: guest.table_id,
        note: guest.note,
        status: guest.status === "1",
        dietary_ids: guest.dietary_ids || [],
        tag_ids: guest.tag_ids || [],
      };

      if (guest.hasOwnProperty('deleted_at')) {
          payload.deleted_at = guest.deleted_at;
      }
      
      return payload;
    });
    
    // Only send deleted IDs that are in pendingDeletedIds
    const deletedIds = Array.from(pendingDeletedIds);
    
    if (updates.length === 0 && deletedIds.length === 0) return;

    setIsSaving(true);
    try {
      await axios.post(
        GUEST_BULK_UPDATE_API,
        { 
          guests: updates,
          deleted_ids: deletedIds,
          eventId: sessionStorage.getItem("event_id")
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        }
      );
      
      setHasSaveError(false);
      setLastSaved(new Date());
      
      const newAutosavedIds = new Set(updates.map(u => u.id));
      setAutosavedGuestIds(prev => {
         const next = new Set(prev);
         newAutosavedIds.forEach(id => next.add(id));
         return next;
      });
      
      // Clear pendingGuestIds for successful updates
      setPendingGuestIds(prev => {
          const next = new Set(prev);
          updates.forEach(u => next.delete(u.id));
          return next;
      });

      setValidationErrors((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        updates.forEach((u) => {
          if (next[u.id]) delete next[u.id];
        });
        return next;
      });

      setAutosavedDeletedIds(prev => {
          const next = new Set(prev);
          deletedIds.forEach(id => next.add(id));
          return next;
      });

      // Clear pendingDeletedIds for successful deletes
      setPendingDeletedIds(prev => {
          const next = new Set(prev);
          deletedIds.forEach(id => next.delete(id));
          return next;
      });

      // Cleanup restored items that are now saved (if they match original)
      setModifiedGuests(prev => {
        const next = { ...prev };
        let changed = false;
        updates.forEach(u => {
             const original = originalGuests[u.id];
             if (original && areGuestsEqual(prev[u.id], original)) {
                 delete next[u.id];
                 changed = true;
             }
        });
        return changed ? next : prev;
      });

    } catch (error) {
      console.error("Autosave failed", error);
      const errorData = error.response?.data;
      const vErrors = parseValidationErrors(errorData, updates);
      if (Object.keys(vErrors).length > 0) {
        setValidationErrors(prev => ({ ...prev, ...vErrors }));
      }
      setHasSaveError(true);
    } finally {
      setIsSaving(false);
    }
  }, [modifiedGuests, pendingGuestIds, pendingDeletedIds, originalGuests, isValidEmail, parseValidationErrors]);

  const latestHandleAutoSave = useRef(handleAutoSave);
  const autosaveTimerRef = useRef(null);

  useEffect(() => {
      latestHandleAutoSave.current = handleAutoSave;
  }, [handleAutoSave]);

  useEffect(() => {
    if (!isAutosaveEnabled) {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      setNextAutosaveTime(null);
      return;
    }

    const hasPending = pendingGuestIds.size > 0 || pendingDeletedIds.size > 0;

    if (hasPending && !autosaveTimerRef.current) {
      const autosaveTime = 5000;
      const nextTime = Date.now() + autosaveTime;
      setNextAutosaveTime(nextTime);
      autosaveTimerRef.current = setTimeout(() => {
        if (latestHandleAutoSave.current) {
          latestHandleAutoSave.current();
        }
        autosaveTimerRef.current = null;
        setNextAutosaveTime(null);
      }, autosaveTime);
    } else if (!hasPending && autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
      setNextAutosaveTime(null);
    }
  }, [isAutosaveEnabled, pendingGuestIds.size, pendingDeletedIds.size]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  const handleInputChange = useCallback((guestId, field, value) => {
    if (!checkPendingLimit(guestId)) return;
    setHasSaveError(false);
    
    setValidationErrors((prev) => {
      const guestErrors = prev?.[guestId];
      if (!guestErrors || !guestErrors[field]) return prev;
      const next = { ...prev };
      const nextGuestErrors = { ...guestErrors };
      delete nextGuestErrors[field];
      if (Object.keys(nextGuestErrors).length === 0) {
        delete next[guestId];
      } else {
        next[guestId] = nextGuestErrors;
      }
      return next;
    });

    setGuestList((prev) => {
      const newList = prev.map((guest) =>
        guest.id === guestId ? { ...guest, [field]: value } : guest
      );

      const updatedGuest = newList.find((g) => g.id === guestId);
      setModifiedGuests((prevModified) => ({
        ...prevModified,
        [guestId]: updatedGuest,
      }));

      return newList;
    });

    setAutosavedGuestIds(prev => {
        const next = new Set(prev);
        if (next.has(guestId)) {
            next.delete(guestId);
            return next;
        }
        return prev;
    });

    setPendingGuestIds(prev => {
        const next = new Set(prev);
        next.add(guestId);
        return next;
    });
  }, []);

  const validateChanges = useCallback(() => {
    const nextErrors = {};
    const invalidIds = [];

    Object.values(safeModifiedGuests || {}).forEach((guest) => {
      if (!guest || safeDeletedGuestIds.has(guest.id)) return;
      const guestErrors = {};

      if (!String(guest.first_name || "").trim()) guestErrors.first_name = true;
      if (!String(guest.last_name || "").trim()) guestErrors.last_name = true;
      if (!isValidEmail(guest.email)) guestErrors.email = true;

      if (Object.keys(guestErrors).length > 0) {
        nextErrors[guest.id] = guestErrors;
        invalidIds.push(guest.id);
      }
    });

    if (invalidIds.length > 0) {
      setValidationErrors(nextErrors);
      toast("Please fix the highlighted fields.", "error");
      const firstInvalidIndex = guestList.findIndex((g) => g?.id === invalidIds[0]);
      if (firstInvalidIndex >= 0 && listRef.current) {
        listRef.current.scrollToRow({ index: firstInvalidIndex, align: "start" });
      }
      return false;
    }

    setValidationErrors({});
    return true;
  }, [safeModifiedGuests, safeDeletedGuestIds, toast, guestList, isValidEmail]);

  const handleBulkUpdate = async () => {
    if (!validateChanges()) return;

    const updates = Array.from(pendingGuestIds).map((id) => {
      const guest = modifiedGuests[id];
      const payload = {
        id: guest.id,
        salutation: guest.salutation,
        first_name: guest.first_name,
        last_name: guest.last_name,
        email: guest.email,
        contact: guest.contact,
        group_name: guest.group_name,
        table_id: guest.table_id,
        note: guest.note,
        status: guest.status === "1",
        dietary_ids: guest.dietary_ids || [],
        tag_ids: guest.tag_ids || [],
      };
      if (guest.hasOwnProperty('deleted_at')) {
          payload.deleted_at = guest.deleted_at;
      }
      return payload;
    }).filter(u => u.id);

    const deletedIds = Array.from(pendingDeletedIds);

    if (updates.length === 0 && deletedIds.length === 0) {
      toast("No changes to save.", "info");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        GUEST_BULK_UPDATE_API,
        { 
          guests: updates,
          deleted_ids: deletedIds,
          eventId: sessionStorage.getItem("event_id")
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        }
      );
      
      setHasSaveError(false);
      setValidationErrors({});
      setLastSaved(new Date());

      // Update autosaved state
      const newAutosavedIds = new Set(updates.map(u => u.id));
      setAutosavedGuestIds(prev => {
         const next = new Set(prev);
         newAutosavedIds.forEach(id => next.add(id));
         return next;
      });

      // Clear pending updates
      setPendingGuestIds(prev => {
          const next = new Set(prev);
          updates.forEach(u => next.delete(u.id));
          return next;
      });

      // Clear pending deletes
      setAutosavedDeletedIds(prev => {
          const next = new Set(prev);
          deletedIds.forEach(id => next.add(id));
          return next;
      });
      setPendingDeletedIds(prev => {
          const next = new Set(prev);
          deletedIds.forEach(id => next.delete(id));
          return next;
      });

      // Clean up modifiedGuests for these IDs if they match original
      setModifiedGuests(prev => {
        const next = { ...prev };
        updates.forEach(u => {
             const original = originalGuests[u.id];
             if (original && areGuestsEqual(prev[u.id], original)) {
                 delete next[u.id];
             }
        });
        return next;
      });

      setLoading(false);
      toast("Guests updated successfully.", "success");
      fetchGuestList(guestListMeta.current_page);
    } catch (error) {
      setLoading(false);
      console.error(error);
      const errorData = error.response?.data;
      const vErrors = parseValidationErrors(errorData, updates);
      if (Object.keys(vErrors).length > 0) {
        setValidationErrors(vErrors);
        toast(errorData?.message || "Validation failed. Please check the highlighted fields.", "error");
        const firstGuestIdWithError = Object.keys(vErrors)[0];
        const firstInvalidIndex = guestList.findIndex((g) => g?.id === firstGuestIdWithError);
        if (firstInvalidIndex >= 0 && listRef.current) {
          listRef.current.scrollToRow({ index: firstInvalidIndex, align: "start" });
        }
      } else {
        toast(errorData?.message || "Failed to update guests.", "error");
      }
    }
  };

  const handlePageClick = (e) => {
    fetchGuestList(e.selected + 1);
  };

  const handleAddRow = () => {
    if (pendingGuestIds.size + pendingDeletedIds.size >= 100) {
        toast("Pending changes limit reached (100). Please save your changes.", "error");
        setShowSaveConfirmModal(true);
        return;
    }

    const newId = `new-${Date.now()}`;
    const newGuest = {
      id: newId,
      salutation: "",
      first_name: "",
      last_name: "",
      email: "",
      contact: "",
      group_name: "",
      table_id: null,
      tag_ids: [],
      dietary_ids: [],
      status: "0",
      isNew: true,
    };
    setGuestList((prev) => [newGuest, ...prev]);
    setModifiedGuests((prev) => ({
      ...prev,
      [newId]: newGuest,
    }));
    setPendingGuestIds((prev) => {
        const next = new Set(prev);
        next.add(newId);
        return next;
    });
    // Scroll to top when adding a new row
    if (listRef.current) {
      listRef.current.scrollToRow({ index: 0, align: "start" });
    }
  };

  const handleRemoveRow = useCallback((guestId) => {
    if (!checkPendingLimit(guestId)) return;

    if (typeof guestId === "string" && guestId.startsWith("new-")) {
      // For new rows, just remove them from the list
      setGuestList((prev) => prev.filter((g) => g.id !== guestId));
      setModifiedGuests((prev) => {
        const newState = { ...prev };
        delete newState[guestId];
        return newState;
      });
      // Also remove from pending if it was there
      setPendingGuestIds((prev) => {
        const next = new Set(prev);
        next.delete(guestId);
        return next;
      });
    } else {
      // For existing rows, mark as deleted
      setDeletedGuestIds((prev) => new Set([...prev, guestId]));
      setPendingDeletedIds((prev) => new Set([...prev, guestId]));
      // Remove from modified if it was there
      setModifiedGuests((prev) => {
        const newState = { ...prev };
        delete newState[guestId];
        return newState;
      });
      // Remove from pending updates since it's deleted now
      setPendingGuestIds((prev) => {
        const next = new Set(prev);
        next.delete(guestId);
        return next;
      });
    }
  }, []);

  const handleRestoreRow = useCallback((guestId) => {
    // Check limit if adding new pending item
    // If guestId is NOT in pending lists, we are adding to pending count (either update or cancel delete which might be complex)
    // Simplified: if not already pending, check limit.
    if (!pendingGuestIds.has(guestId) && !pendingDeletedIds.has(guestId)) {
        if (pendingGuestIds.size + pendingDeletedIds.size >= 100) {
            toast("Pending changes limit reached (100). Please save your changes.", "error");
            setShowSaveConfirmModal(true);
            return;
        }
    }

    if (deletedGuestIds.has(guestId)) {
      if (autosavedDeletedIds.has(guestId)) {
        const original = originalGuests[guestId];
        if (original) {
          // Restore data to modifiedGuests
          setModifiedGuests((prev) => ({
                  ...prev,
                  [guestId]: { ...original, deleted_at: null },
                }));
                // Remove from autosavedDeletedIds since we are undeleting it
                setAutosavedDeletedIds((prev) => {
                  const next = new Set(prev);
                  next.delete(guestId);
                  return next;
                });
                // Also remove from pendingDeletedIds if it's there
                setPendingDeletedIds((prev) => {
                   const next = new Set(prev);
                   next.delete(guestId);
                   return next;
                });
                // Mark as a pending update so it gets sent to backend to be restored/re-created
                setPendingGuestIds((prev) => {
                  const next = new Set(prev);
                  next.add(guestId);
                  return next;
                });
        }
      }
      // Remove from UI deleted list
      setDeletedGuestIds((prev) => {
        const next = new Set(prev);
        next.delete(guestId);
        return next;
      });
      // Remove from pending deleted list (if it wasn't saved yet)
      setPendingDeletedIds((prev) => {
        const next = new Set(prev);
        next.delete(guestId);
        return next;
      });
    } else if (modifiedGuests[guestId]) {
            // Restore to original values
            const original = originalGuests[guestId];
            if (original) {
              setGuestList((prev) =>
                prev.map((g) => (g.id === guestId ? { ...original } : g))
              );
              // Force update in modifiedGuests so it gets autosaved (reverted in DB)
              setModifiedGuests((prev) => ({
                   ...prev,
                   [guestId]: { ...original }
              }));
              setPendingGuestIds((prev) => {
                  const next = new Set(prev);
                  next.add(guestId);
                  return next;
              });
              // Remove from autosaved so it shows as pending
              setAutosavedGuestIds(prev => {
                   const next = new Set(prev);
                   next.delete(guestId);
                   return next;
              });
            }
          }
  }, [deletedGuestIds, modifiedGuests, originalGuests, autosavedDeletedIds]);

  const handlePerPageChange = (e) => {
    const nextPerPage = Number(e.target.value);
    setPerPage(nextPerPage);
    fetchGuestList(1, nextPerPage);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedTags([]);
    setSelectedTables([]);
    setSelectedStatus([]);
  };

  const isFilterActive = useMemo(() => {
    return (
      searchTerm.trim() !== "" ||
      selectedTags.length > 0 ||
      selectedTables.length > 0 ||
      selectedStatus.length > 0
    );
  }, [searchTerm, selectedTags, selectedTables, selectedStatus]);

  const breadcrumbItems = [
    { label: "Guest List", to: GUEST_LIST },
    { label: "Bulk Edit" },
  ];

  const rowProps = useMemo(() => ({
    guestList,
    handleRemoveRow,
    handleRestoreRow,
    handleInputChange,
    safeDeletedGuestIds,
    safeModifiedGuests,
    autosavedGuestIds,
    tableOptions,
    tagOptions,
    dietaryOptions,
    validationErrors,
  }), [
    guestList,
    handleRemoveRow,
    handleRestoreRow,
    handleInputChange,
    safeDeletedGuestIds,
    safeModifiedGuests,
    autosavedGuestIds,
    tableOptions,
    tagOptions,
    dietaryOptions,
    validationErrors,
  ]);

  const syncHorizontalScroll = useCallback((source, scrollLeft) => {
    if (isSyncingScrollRef.current) return;
    isSyncingScrollRef.current = true;

    if (source === "header") {
      if (listElement) listElement.scrollLeft = scrollLeft;
    } else {
      const headerEl = headerScrollRef.current;
      if (headerEl) headerEl.scrollLeft = scrollLeft;
    }

    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
    });
  }, [listElement]);

  const handleListResize = useCallback(() => {
    const el = listRef.current?.element || null;
    setListElement((prev) => (prev === el ? prev : el));
  }, []);

  useEffect(() => {
    const headerEl = headerScrollRef.current;
    if (!headerEl) return;

    const onHeaderScroll = () => {
      syncHorizontalScroll("header", headerEl.scrollLeft);
    };

    headerEl.addEventListener("scroll", onHeaderScroll, { passive: true });
    return () => headerEl.removeEventListener("scroll", onHeaderScroll);
  }, [syncHorizontalScroll]);

  useEffect(() => {
    if (!listElement) return;

    const onBodyScroll = () => {
      syncHorizontalScroll("body", listElement.scrollLeft);
    };

    listElement.addEventListener("scroll", onBodyScroll, { passive: true });
    const headerEl = headerScrollRef.current;
    if (headerEl) headerEl.scrollLeft = listElement.scrollLeft;

    return () => listElement.removeEventListener("scroll", onBodyScroll);
  }, [listElement, syncHorizontalScroll]);

  const handleTableWheel = useCallback(
    (e) => {
      const deltaX = e.deltaX || 0;
      const deltaY = e.deltaY || 0;
      const delta = deltaX !== 0 ? deltaX : e.shiftKey ? deltaY : 0;
      if (!delta) return;
      if (!e.shiftKey && Math.abs(deltaX) < Math.abs(deltaY)) return;

      const headerEl = headerScrollRef.current;
      const currentLeft =
        listElement?.scrollLeft ?? headerEl?.scrollLeft ?? 0;
      const nextLeft = Math.max(0, currentLeft + delta);

      if (listElement) listElement.scrollLeft = nextLeft;
      if (headerEl) headerEl.scrollLeft = nextLeft;

      if (e.cancelable && typeof e.preventDefault === "function") e.preventDefault();
    },
    [listElement]
  );

  return (
    <div className={`flex flex-col w-full h-full overflow-hidden ${isFullscreen ? "fixed inset-0 z-[100] bg-white" : ""}`}>
      <LoadingInComponent isLoading={loading}>
        <div className={`flex flex-col w-full h-full no-scrollbar overflow-y-auto ${isFullscreen ? "p-0" : "p-[16px] md:p-[32px] gap-[24px]"}`}>
          {!isFullscreen && (
            <>
              <Breadcrumb items={breadcrumbItems} />

              <div className="flex flex-col md:flex-row w-full justify-between items-center gap-4">
                <h1 className="text-[29px]">Bulk Edit Guests</h1>
                <div className="flex gap-[8px] items-center">
                  <Button variant={isFilterActive ? "primary" : "secondary"} onClick={() => setShowFilterModal(true)} className="relative">
                  <FilterSearch size={16} /> Filter
                  {isFilterActive && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                  )}
                </Button>
                  <Button variant="secondary" onClick={() => setIsFullscreen(true)}>
                    <Maximize4 size={16} /> Fullscreen
                  </Button>
                  <Button variant="secondary" onClick={handleAddRow}>
                    + New
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setShowSaveConfirmModal(true)}
                    disabled={!hasUnsavedChanges}
                  >
                    Save
                  </Button>
                  <Button variant="secondary" onClick={() => navigate(GUEST_LIST)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </>
          )}

          {isFullscreen && (
            <>
            <div className="fixed bottom-8 right-4 z-[110] flex flex-col items-center gap-2">
              {/* Expandable Menu Items */}
              <div className={`flex flex-col items-center gap-2 transition-all duration-300 ${isFabOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"}`}>
                <button
                  onClick={() => {
                    setIsFullscreen(false);
                    setIsFabOpen(false);
                  }}
                  className="p-2.5 bg-white text-[#1A1C1E] rounded-full shadow-xl hover:bg-gray-100 transition-all hover:scale-110 active:scale-90 border border-[#EFF0F2]"
                  title="Exit Fullscreen"
                >
                  <Maximize4 size={20} variant="Linear" />
                </button>
                <button
                  onClick={() => {
                    setShowSaveConfirmModal(true);
                    setIsFabOpen(false);
                  }}
                  disabled={Object.keys(safeModifiedGuests).length === 0 && safeDeletedGuestIds.size === 0}
                  className={`p-2.5 bg-[#0CAF60] text-white rounded-full shadow-xl hover:bg-[#099351] transition-all hover:scale-110 active:scale-90 ${
                    (Object.keys(safeModifiedGuests).length === 0 && safeDeletedGuestIds.size === 0) ? "opacity-50 cursor-not-allowed grayscale" : ""
                  }`}
                  title="Save Changes"
                 >
                   <TickSquare size={20} variant="Linear" />
                 </button>
                <button
                  onClick={() => {
                    setShowFilterModal(true);
                    setIsFabOpen(false);
                  }}
                  className={`p-2.5 rounded-full shadow-xl transition-all hover:scale-110 active:scale-90 relative border border-[#EFF0F2] ${
                    isFilterActive ? "bg-white text-[#5C4BF5]" : "bg-white text-[#1A1C1E] hover:bg-gray-50"
                  }`}
                  title="Filter Guests"
                >
                  <FilterSearch size={20} variant="Linear" />
                  {isFilterActive && (
                    <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                  )}
                </button>
                <button
                  onClick={() => {
                    handleAddRow();
                    setIsFabOpen(false);
                  }}
                  className="p-2.5 bg-white text-[#1A1C1E] border border-[#EFF0F2] rounded-full shadow-xl hover:bg-gray-50 transition-all hover:scale-110 active:scale-90"
                  title="Add New Row"
                >
                  <Add size={20} variant="Linear" />
                </button>
              </div>

              {/* Main FAB Trigger */}
              <button
                onClick={() => setIsFabOpen(!isFabOpen)}
                className="relative p-3 bg-[#5C4BF5] text-white rounded-full shadow-xl transition-transform duration-300 hover:scale-110 active:scale-95 hover:bg-[#4A3CC9]"
                title={isFabOpen ? "Close Menu" : "Open Actions"}
              >
                <span className={`flex transition-transform duration-300 ${isFabOpen ? "rotate-45" : "rotate-0"}`}>
                  <Add size={24} variant="Linear" />
                </span>
                {isFilterActive && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>
                )}
              </button>
            </div>
          </>
          )}

          <div className={`flex flex-col border border-[#EFF0F2] w-full bg-white rounded-xl overflow-hidden ${isFullscreen ? "flex-1 rounded-none border-none" : "h-[600px]"}`}>
            <div className="w-full flex-1 flex flex-col min-h-0">
              <div className="flex-1 w-full min-h-0 relative bg-white overflow-hidden" onWheel={handleTableWheel}>
                <AutoSizer
                  style={{ height: "100%", width: "100%" }}
                  renderProp={({ height, width }) => (
                    <div className="h-full w-full flex flex-col">
                      <div
                        ref={headerScrollRef}
                        className="shrink-0 w-full overflow-x-auto overflow-y-hidden bg-[#F7F8F9] border-b border-[#EFF0F2] font-bold text-sm no-scrollbar not-scrollbar"
                        style={{ height: HEADER_HEIGHT }}
                      >
                        <div className="flex items-center min-w-[1710px]" style={{ height: HEADER_HEIGHT }}>
                          <div className="flex items-center sticky left-0 z-20 bg-[#F7F8F9] border-r border-[#EFF0F2]">
                            <div className="px-[6px] py-[4px] w-[50px] shrink-0"></div>
                            <div className="px-[6px] py-[4px] w-[80px] shrink-0">Title</div>
                            <div className="px-[6px] py-[4px] w-[150px] shrink-0">First Name</div>
                            <div className="px-[6px] py-[4px] w-[150px] shrink-0">Last Name</div>
                          </div>
                          <div className="px-[6px] py-[4px] w-[200px] shrink-0">Email</div>
                          <div className="px-[6px] py-[4px] w-[150px] shrink-0">Contact</div>
                          <div className="px-[6px] py-[4px] w-[150px] shrink-0">Group</div>
                          <div className="px-[6px] py-[4px] w-[180px] shrink-0">Table</div>
                          <div className="px-[6px] py-[4px] w-[220px] shrink-0">Tags</div>
                          <div className="px-[6px] py-[4px] w-[220px] shrink-0">Dietary</div>
                          <div className="px-[6px] py-[4px] w-[120px] shrink-0">Status</div>
                        </div>
                      </div>
                      <div className="flex-1 min-h-0">
                        <List
                          listRef={listRef}
                          rowCount={guestList.length}
                          rowHeight={ROW_HEIGHT}
                          rowComponent={GuestRow}
                          rowProps={rowProps}
                          height={Math.max(0, (height || 400) - HEADER_HEIGHT)}
                          width={width || TABLE_WIDTH}
                          onResize={handleListResize}
                          style={{ pointerEvents: "auto" }}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>
            {guestListMeta && (
              <Paginator
                meta={guestListMeta}
                handlePageClick={handlePageClick}
                handlePerPageChange={handlePerPageChange}
                perPage={perPage}
                perPageOptions={[1000, 2000, 3000, 4000, 5000]}
              >
                  <div className="flex items-center gap-4">
                     <LastSavedIndicator 
                        lastSaved={lastSaved} 
                        isSaving={isSaving} 
                        nextAutosaveTime={nextAutosaveTime}
                        hasSaveError={hasSaveError}
                        hasPendingChanges={pendingGuestIds.size > 0 || pendingDeletedIds.size > 0}
                     />
                     <div className="flex items-center gap-2" title="Toggle Autosave">
                       <span className="text-gray-500 text-sm">Autosave</span>
                       <button 
                         className={`w-9 h-5 rounded-full p-0.5 transition-colors relative ${isAutosaveEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                         onClick={() => setIsAutosaveEnabled(!isAutosaveEnabled)}
                       >
                         <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 absolute top-0.5 left-0.5 ${isAutosaveEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                       </button>
                     </div>
                  </div>
              </Paginator>
            )}
          </div>
        </div>
      </LoadingInComponent>

      {showFilterModal && (
        <Modal
          toggleModal={() => {
            setShowFilterModal(false);
            fetchGuestList(1);
          }}
          title="Filter Guests"
          cancelLabel="Close"
          hideCloseIcon
          leftActions={
            <Button
              variant="warning"
              onClick={() => {
                clearFilters();
                fetchGuestList(1);
              }}
            >
              Clear All Filters
            </Button>
          }
        >
          <div className="flex flex-col gap-4 w-full">
            <FilterTextInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
            />
            
            <DropdownSet
              label="Tags"
              options={tagOptions}
              value={selectedTags}
              isMulti
              onChange={(_, val) => setSelectedTags(val)}
            />

            <DropdownSet
              label="Table"
              options={tableOptions}
              value={selectedTables}
              isMulti
              placeholder="Select Tables"
              onChange={(_, val) => setSelectedTables(val)}
            />

            <DropdownSet
              label="Status"
              options={statusOptions}
              value={selectedStatus}
              isMulti
              onChange={(_, val) => setSelectedStatus(val)}
            />
          </div>
        </Modal>
      )}

      {showSaveConfirmModal && (
        <Modal
          toggleModal={() => setShowSaveConfirmModal(false)}
          title="Confirm Changes"
          handleSubmit={() => {
            setShowSaveConfirmModal(false);
            handleBulkUpdate();
          }}
          submitLabel="Save All"
        >
          <div className="flex flex-col gap-4">
            <p className="text-gray-600">
              Are you sure you want to save the following unsaved changes?
            </p>
            <div className="flex flex-col gap-2 text-sm">
              {newGuestCount > 0 && (
                <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                  <span className="text-green-700 font-medium">New Guests</span>
                  <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded-full">{newGuestCount}</span>
                </div>
              )}
              {modifiedExistingCount > 0 && (
                <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                  <span className="text-blue-700 font-medium">Modified Guests</span>
                  <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">{modifiedExistingCount}</span>
                </div>
              )}
              {pendingDeletedCount > 0 && (
                <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                  <span className="text-red-700 font-medium">Deleted Guests</span>
                  <span className="bg-red-200 text-red-800 px-2 py-0.5 rounded-full">{pendingDeletedCount}</span>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default GuestBulkEditPage;
